import React, { useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import LegalBackToLogin from "../components/LegalBackToLogin";
import { ThemeContext } from "../context/ThemeContext";

const makeStyles = (themeColors: any): Record<string, React.CSSProperties> => ({
  page: {
    minHeight: "100vh",
    background: themeColors.bg,
    color: themeColors.text,
    padding: "48px 18px",
    fontFamily: "Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  },
  card: {
    maxWidth: 980,
    margin: "0 auto",
    border: `1px solid ${themeColors.border}`,
    background: themeColors.panel,
    borderRadius: 28,
    boxShadow: themeColors.glow,
    padding: "34px clamp(20px, 4vw, 48px)",
    backdropFilter: "blur(18px)",
  },
  eyebrow: {
    color: themeColors.accent2,
    letterSpacing: 2,
    textTransform: "uppercase",
    fontSize: 12,
    fontWeight: 800,
    marginBottom: 10,
  },
  title: {
    fontSize: "clamp(32px, 5vw, 52px)",
    lineHeight: 1.02,
    margin: "0 0 10px",
    color: themeColors.text,
  },
  date: {
    color: themeColors.mutedText,
    marginBottom: 28,
    fontSize: 15,
  },
  section: {
    marginTop: 28,
    paddingTop: 22,
    borderTop: `1px solid ${themeColors.border}`,
  },
  h2: {
    color: themeColors.accent,
    fontSize: 22,
    margin: "0 0 10px",
  },
  p: {
    color: themeColors.text,
    fontSize: 16,
    lineHeight: 1.75,
    margin: "8px 0",
  },
  list: {
    color: themeColors.text,
    lineHeight: 1.75,
    paddingLeft: 22,
  },
  nav: {
    display: "flex",
    gap: 14,
    flexWrap: "wrap",
    marginTop: 36,
    paddingTop: 24,
    borderTop: `1px solid ${themeColors.border}`,
  },
  link: {
    color: themeColors.accent2,
    textDecoration: "none",
    fontWeight: 800,
  },
  footer: {
    marginTop: 22,
    color: themeColors.mutedText,
    fontSize: 13,
    lineHeight: 1.6,
  },
});

export default function Credits() {
  const { themeColors } = useContext(ThemeContext);
  const styles = useMemo(() => makeStyles(themeColors), [themeColors]);

  return (
    <main style={styles.page}>
      <article style={styles.card}>
        <div style={styles.eyebrow}>AVAAIO Legal</div>
        <h1 style={styles.title}>Credits & Acknowledgments</h1>
        <div style={styles.date}>Effective Date: May 7, 2026</div>


        <section style={styles.section}>
          <h2 style={styles.h2}>Thank You</h2>
          <p style={styles.p}>
            AVAAIO™ is built with appreciation for the developers, designers, researchers, open-source maintainers, educators, and technology communities whose work helps make modern AI software possible.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Platform</h2>
          <p style={styles.p}>
            AVAAIO™ is built and operated by American Globalo, LLC. Ava is the AI assistant experience inside the AVAAIO™ platform.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Technologies and Communities</h2>
          <ul style={styles.list}>
            <li>OpenAI and modern AI research communities</li>
            <li>React, Vite, TypeScript, JavaScript, Node.js, and web platform contributors</li>
            <li>Three.js and 3D web graphics contributors</li>
            <li>FastAPI, Python, Linux, Nginx, and cloud infrastructure communities</li>
            <li>Speech, accessibility, security, and open-source software communities</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Third-Party Rights</h2>
          <p style={styles.p}>
            Third-party names, trademarks, frameworks, libraries, services, and technologies remain the property of their respective owners. References are provided for acknowledgment and transparency only and do not imply endorsement.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Creator Acknowledgment</h2>
          <p style={styles.p}>
            AVAAIO™ exists because of continued work, testing, vision, and dedication toward building a helpful, safer, and more capable AI assistant experience.
          </p>
        </section>


          <LegalBackToLogin />
        <nav style={styles.nav} aria-label="Legal navigation">
          <Link style={styles.link} to="/privacy-policy">Privacy Policy</Link>
          <Link style={styles.link} to="/terms-and-conditions">Terms & Conditions</Link>
          <Link style={styles.link} to="/ai-disclaimer">AI Disclaimer</Link>
          <Link style={styles.link} to="/credits">Credits & Acknowledgments</Link>
        </nav>

        <footer style={styles.footer}>
          © 2026 American Globalo, LLC. All rights reserved.<br />
          AVAAIO™ is owned and operated by American Globalo, LLC. Ava is the AI assistant experience within the AVAAIO™ platform.<br />
          Support: support@avaaio.com
        </footer>
      </article>
    </main>
  );
}
